import Ajv from 'ajv'
import type { JSONSchemaType } from './resolveEffectiveSchema'
import { resolveEffectiveSchema } from './resolveEffectiveSchema'

/**
 * Strip properties from a model that are not present in the effective schema.
 * This ensures the model only contains data for fields that are currently
 * active/visible based on conditional evaluation.
 *
 * @param model The current form model data
 * @param effectiveSchema The resolved effective schema
 * @returns A new model with only properties present in the effective schema
 */
export function stripModel<T extends Record<string, unknown>>(
  model: T,
  effectiveSchema: JSONSchemaType,
  ajv?: Ajv
): Partial<T> {
  if (!effectiveSchema.properties) {
    // If the schema has no properties definition, it's a scalar or untyped — return as-is
    if (effectiveSchema.type === 'object') {
      return {} as Partial<T>
    }
    return model
  }

  const result: Record<string, unknown> = {}
  const validProperties = new Set(Object.keys(effectiveSchema.properties))

  for (const [key, value] of Object.entries(model)) {
    if (!validProperties.has(key)) {
      // Property not in effective schema — strip it
      continue
    }

    const propSchema = effectiveSchema.properties[key]

    if (
      propSchema.type === 'object' &&
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      // Recursively strip nested objects
      result[key] = stripModel(value as Record<string, unknown>, propSchema)
    } else if (
      propSchema.type === 'array' &&
      Array.isArray(value) &&
      propSchema.items &&
      !Array.isArray(propSchema.items)
    ) {
      const itemsTemplate = propSchema.items as JSONSchemaType
      // For arrays, resolve each item's effective schema against its own data,
      // then strip. This ensures per-item conditionals are respected.
      if (itemsTemplate.type === 'object') {
        result[key] = value.map(item => {
          if (item !== null && typeof item === 'object') {
            const itemEffective = resolveEffectiveSchema(itemsTemplate, item, itemsTemplate, ajv)
            return stripModel(item as Record<string, unknown>, itemEffective, ajv)
          }
          return item
        })
      } else {
        result[key] = value
      }
    } else {
      result[key] = value
    }
  }

  return result as Partial<T>
}

/**
 * Strip an individual array item's data against its effective schema.
 */
export function stripArrayItem(
  itemData: Record<string, unknown>,
  effectiveItemSchema: JSONSchemaType
): Record<string, unknown> {
  return stripModel(itemData, effectiveItemSchema)
}
